import maplibregl from "maplibre-gl";
import type { FeatureCollection, LineString } from "geojson";
import {
  kilometresToNauticalMiles,
  routeDistanceKm,
  type RoutePoint,
} from "../routes/routes";

const SOURCE_ID = "klint-route";
const LAYER_ID = "klint-route-line";
const MAX_ROUTE_POINTS = 200;

export interface RouteEditorState {
  pointCount: number;
  points: RoutePoint[];
  nauticalMiles: number;
  kilometres: number;
}

interface EditablePoint {
  marker: maplibregl.Marker;
  element: HTMLElement;
  location: maplibregl.LngLat;
}

function lineData(points: EditablePoint[]): FeatureCollection<LineString> {
  if (points.length < 2) return { type: "FeatureCollection", features: [] };
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: {},
        geometry: {
          type: "LineString",
          coordinates: points.map(({ location }) => [location.lng, location.lat]),
        },
      },
    ],
  };
}

/** A draggable waypoint editor and persistent route drawing above the chart. */
export class RouteEditor {
  private readonly points: EditablePoint[] = [];
  private started = false;
  private editing = false;

  private readonly addPointAtClick = (event: maplibregl.MapMouseEvent): void => {
    if (this.points.length >= MAX_ROUTE_POINTS) return;
    this.addPoint(event.lngLat);
  };

  constructor(
    private readonly map: maplibregl.Map,
    private readonly onChange: (state: RouteEditorState) => void,
  ) {}

  start(initialPoints: RoutePoint[] = []): void {
    if (this.started) return;
    this.started = true;
    this.ensureLine();
    for (const [longitude, latitude] of initialPoints.slice(0, MAX_ROUTE_POINTS)) {
      this.addPoint(new maplibregl.LngLat(longitude, latitude), false);
    }
    this.setEditing(true);
    this.update();
  }

  setEditing(editing: boolean): void {
    if (!this.started || this.editing === editing) return;
    this.editing = editing;
    this.map.getContainer().classList.toggle("is-editing-route", editing);
    if (editing) this.map.on("click", this.addPointAtClick);
    else this.map.off("click", this.addPointAtClick);
    for (const point of this.points) {
      point.marker.setDraggable(editing);
      point.element.classList.toggle("is-readonly", !editing);
    }
  }

  undo(): void {
    this.points.pop()?.marker.remove();
    this.renumber();
    this.update();
  }

  clear(): void {
    for (const { marker } of this.points) marker.remove();
    this.points.length = 0;
    this.update();
  }

  coordinates(): RoutePoint[] {
    return this.points.map(({ location }) => [location.lng, location.lat]);
  }

  setTarget(index: number | null): void {
    for (let pointIndex = 0; pointIndex < this.points.length; pointIndex++) {
      this.points[pointIndex].element.classList.toggle("is-route-target", pointIndex === index);
    }
  }

  setManOverboard(index: number | null): void {
    for (let pointIndex = 0; pointIndex < this.points.length; pointIndex++) {
      const active = pointIndex === index;
      const element = this.points[pointIndex].element;
      element.classList.toggle("is-man-overboard", active);
      if (active) {
        element.textContent = "MOB";
        element.setAttribute("aria-label", "Man overboard position");
        element.title = "Man overboard position";
      }
    }
  }

  destroy(): void {
    if (this.editing) this.map.off("click", this.addPointAtClick);
    this.editing = false;
    this.started = false;
    this.map.getContainer().classList.remove("is-editing-route");
    for (const { marker } of this.points) marker.remove();
    this.points.length = 0;
    if (this.map.getLayer(LAYER_ID)) this.map.removeLayer(LAYER_ID);
    if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID);
  }

  private addPoint(location: maplibregl.LngLat, update = true): void {
    const element = document.createElement("div");
    element.className = "route-waypoint";
    element.textContent = String(this.points.length + 1);
    element.setAttribute("role", "img");
    element.setAttribute(
      "aria-label",
      `Route waypoint ${this.points.length + 1}. Drag to adjust.`,
    );
    element.title = "Drag to adjust this waypoint";

    const marker = new maplibregl.Marker({
      element,
      anchor: "center",
      draggable: this.editing,
    })
      .setLngLat(location)
      .addTo(this.map);
    const point = { marker, element, location };
    marker.on("drag", () => {
      point.location = marker.getLngLat();
      this.update();
    });
    this.points.push(point);
    if (update) this.update();
  }

  private renumber(): void {
    for (let index = 0; index < this.points.length; index++) {
      const { element } = this.points[index];
      element.textContent = String(index + 1);
      element.setAttribute("aria-label", `Route waypoint ${index + 1}. Drag to adjust.`);
    }
  }

  private ensureLine(): void {
    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: "geojson",
        data: lineData(this.points),
      });
    }
    if (!this.map.getLayer(LAYER_ID)) {
      this.map.addLayer({
        id: LAYER_ID,
        type: "line",
        source: SOURCE_ID,
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": "#ffb703",
          "line-opacity": 0.95,
          "line-width": 4,
        },
      });
    }
  }

  private update(): void {
    (this.map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined)?.setData(
      lineData(this.points),
    );
    const points = this.coordinates();
    const kilometres = routeDistanceKm(points);
    this.onChange({
      pointCount: points.length,
      points,
      kilometres,
      nauticalMiles: kilometresToNauticalMiles(kilometres),
    });
  }
}
